import { useMemo } from 'react';
import { PurchaseOrderItem } from '@domain/purchasing';
import { isSameWeek, startOfWeek, addWeeks, subWeeks, min, max, isValid } from 'date-fns';

interface UseTimelineOptions {
    maxNumberOfWeeks?: number;
    weekWidth?: number;
    headerColumnWidth?: number;
}

interface TimelineWeeks {
    weeks: Date[];
    currentWeekIndex: number;
}

interface TimelinePosition {
    getPositionsForDate: (date: Date) => number;
    getEndDateForItem: (item: PurchaseOrderItem) => Date | undefined;
    getItemDuration: (item: PurchaseOrderItem) => number;
    getItemStartPosition: (item: PurchaseOrderItem) => number;
    getItemEndPosition: (item: PurchaseOrderItem) => number;
    weekWidth: number;
    headerColumnWidth: number;
}

export const useTimeline = (options: UseTimelineOptions = {}): TimelineWeeks & TimelinePosition => {
    const {
        maxNumberOfWeeks = 52,
        weekWidth = 200,
        headerColumnWidth = 300
    } = options;

    // Generate weeks for x-axis (52 weeks past + 52 weeks future)
    const { weeks, currentWeekIndex } = useMemo(() => {
        const currentDate = new Date();
        const currentWeekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        const weeks: Date[] = [];

        // Add 52 weeks in the past
        for (let i = maxNumberOfWeeks; i > 0; i--) {
            weeks.push(subWeeks(currentWeekStart, i));
        }

        // Add current week
        weeks.push(currentWeekStart);

        // Add 52 weeks in the future
        for (let i = 1; i <= 52; i++) {
            weeks.push(addWeeks(currentWeekStart, i));
        }

        return {
            weeks,
            currentWeekIndex: maxNumberOfWeeks // Current week is at index maxNumberOfWeeks
        };
    }, [maxNumberOfWeeks]);

    const getPositionsForDate = (date: Date): number => {
        const weekIndex = weeks.findIndex(week => isSameWeek(week, date));

        if (weekIndex === -1) {
            // If date is not found in weeks, return a fallback position
            return headerColumnWidth;
        }

        const dayOfWeek = date.getDay();
        return weekIndex * weekWidth + headerColumnWidth + (dayOfWeek * (weekWidth / 7));
    };

    const getEndDateForItem = (item: PurchaseOrderItem): Date | undefined => {

        if (item.shipment) {
            return item.shipment.processedDate ? new Date(item.shipment.processedDate) : item.shipment.arrivalDate ? new Date(item.shipment.arrivalDate) : item.shipment.departureDate ? new Date(item.shipment.departureDate) : undefined;
        }
        if (item.eta) {
            return new Date(item.eta);
        }
        if (item.productionReadyDate) {
            return new Date(item.productionReadyDate);
        }
        if (item.productionStartDate) {
            return new Date(item.productionStartDate);
        }

        return undefined;
    };

    const getStartDateForItem = (item: PurchaseOrderItem): Date | undefined => {
        const dates = [
            item.productionStartDate ? new Date(item.productionStartDate) : null,
            item.productionReadyDate ? new Date(item.productionReadyDate) : null,
            item.shipment?.processedDate ? new Date(item.shipment.processedDate) : null,
            item.shipment?.arrivalDate ? new Date(item.shipment.arrivalDate) : null,
            item.shipment?.departureDate ? new Date(item.shipment.departureDate) : null
        ].filter((date): date is Date => date !== null && isValid(date));

        return dates.length > 0 ? min(dates) : undefined;
    };

    const getItemDuration = (item: PurchaseOrderItem): number => {
        const startDate = item.productionStartDate;
        const endDate = getEndDateForItem(item);

        if (!startDate || !endDate) return 0;

        const startPosition = getPositionsForDate(startDate);
        const endPosition = getPositionsForDate(endDate);

        return endPosition - startPosition;
    };

    const getItemStartPosition = (item: PurchaseOrderItem): number => {
        const startDate = getStartDateForItem(item);
        if (!startDate) {
            // If no valid start date, use current date as fallback
            return getPositionsForDate(new Date());
        }
        return getPositionsForDate(startDate);
    };

    const getItemEndPosition = (item: PurchaseOrderItem): number => {
        const endDate = getEndDateForItem(item);
        if (!endDate) {
            // If no valid end date, use current date + 1 week as fallback
            const fallbackDate = new Date();
            fallbackDate.setDate(fallbackDate.getDate() + 7);
            return getPositionsForDate(fallbackDate);
        }
        return getPositionsForDate(endDate);
    };

    return {
        weeks,
        currentWeekIndex,
        weekWidth,
        headerColumnWidth,
        getPositionsForDate,
        getEndDateForItem,
        getItemDuration,
        getItemStartPosition,
        getItemEndPosition
    };
};
